import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, FileText, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  CATEGORIES,
  CATEGORY_LABEL,
  CATEGORY_TONE,
  type DocumentCategory,
  type DocumentSummary,
} from './documents/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const MONTH_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

const MONTH_LONG = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export function Documents() {
  const documents = useQuery({
    queryKey: ['documents', 'mine'],
    queryFn: () => api<DocumentSummary[]>('/documents'),
  });

  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | 'ALL'>('ALL');
  const [yearFilter, setYearFilter] = useState<number | 'ALL'>('ALL');
  const [monthFilter, setMonthFilter] = useState<number | 'ALL'>('ALL');

  const years = useMemo(() => {
    const set = new Set<number>();
    for (const d of documents.data ?? []) {
      if (d.periodYear) set.add(d.periodYear);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [documents.data]);

  const visible = useMemo(() => {
    return (documents.data ?? []).filter((d) => {
      if (categoryFilter !== 'ALL' && d.category !== categoryFilter) return false;
      if (yearFilter !== 'ALL' && d.periodYear !== yearFilter) return false;
      if (monthFilter !== 'ALL' && d.periodMonth !== monthFilter) return false;
      return true;
    });
  }, [documents.data, categoryFilter, yearFilter, monthFilter]);

  // Selección múltiple para descarga masiva en ZIP. El empleado no puede
  // borrar sus propios documentos, así que la barra solo expone descargar.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected((prev) => {
      const inView = new Set(visible.map((d) => d.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (inView.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visible]);

  const allSelected = visible.length > 0 && visible.every((d) => selected.has(d.id));
  const someSelected = selected.size > 0 && !allSelected;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visible.map((d) => d.id)));
  }
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const bulkDownload = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      const res = await fetch(`${API_BASE_URL}/documents/bulk-download`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const data: { message?: string | string[] } = await res.json().catch(() => ({}));
        const msg = Array.isArray(data.message) ? data.message[0] : data.message;
        throw new Error(msg ?? `Error ${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i) ?? cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? decodeURIComponent(m[1]) : 'documentos.zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err: unknown) => {
      window.alert(err instanceof Error ? err.message : 'Error al descargar');
    },
  });

  return (
    <div className="p-6 md:p-8">
      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border">
          <h2 className="text-lg font-semibold tracking-tightish">Documentos</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Nóminas, contratos y otros documentos que la empresa pone a tu disposición.
          </p>
        </header>

        <div className="px-7 py-4 border-b border-border flex flex-wrap items-center gap-3 bg-paper/40">
          <FilterSelect
            label="Categoría"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v as DocumentCategory | 'ALL')}
            options={[
              { value: 'ALL', label: 'Todas' },
              ...CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
            ]}
          />
          {years.length > 0 ? (
            <FilterSelect
              label="Año"
              value={String(yearFilter)}
              onChange={(v) => setYearFilter(v === 'ALL' ? 'ALL' : Number(v))}
              options={[
                { value: 'ALL', label: 'Todos' },
                ...years.map((y) => ({ value: String(y), label: String(y) })),
              ]}
            />
          ) : null}
          <FilterSelect
            label="Mes"
            value={String(monthFilter)}
            onChange={(v) => setMonthFilter(v === 'ALL' ? 'ALL' : Number(v))}
            options={[
              { value: 'ALL', label: 'Todos' },
              ...MONTH_LONG.map((label, idx) => ({ value: String(idx + 1), label })),
            ]}
          />
        </div>

        <div className="px-7 py-5">
          {documents.isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : documents.error ? (
            <p role="alert" className="text-sm text-destructive">
              {documents.error instanceof ApiError
                ? documents.error.message
                : 'Error cargando documentos'}
            </p>
          ) : visible.length === 0 ? (
            <EmptyState
              empty={(documents.data?.length ?? 0) === 0}
              hasFilters={categoryFilter !== 'ALL' || yearFilter !== 'ALL'}
            />
          ) : (
            <>
              {selected.size > 0 ? (
                <div className="-mx-7 px-7 py-2.5 mb-3 bg-icaria-100/50 border-y border-border flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-[13px] font-medium text-icaria-700">
                    {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => bulkDownload.mutate()}
                      disabled={bulkDownload.isPending}
                    >
                      <Download className="h-3.5 w-3.5" />
                      {bulkDownload.isPending ? 'Preparando…' : `Descargar (${selected.size})`}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelected(new Set())}
                      disabled={bulkDownload.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                      Limpiar
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="-mx-7 overflow-x-auto">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr>
                      <Th align="center">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todos"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected;
                          }}
                          onChange={toggleAll}
                          className="h-4 w-4 accent-icaria-600 cursor-pointer"
                        />
                      </Th>
                      <Th>Categoría</Th>
                      <Th>Documento</Th>
                      <Th align="center">Periodo</Th>
                      <Th align="right">Tamaño</Th>
                      <Th>Subido</Th>
                      <Th align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((doc) => (
                      <DocumentRow
                        key={doc.id}
                        doc={doc}
                        selected={selected.has(doc.id)}
                        onToggleSelect={() => toggleOne(doc.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DocumentRow({
  doc,
  selected,
  onToggleSelect,
}: {
  doc: DocumentSummary;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/documents/${doc.id}/download`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('No se pudo descargar el documento');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error al descargar');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <tr
      className={cn(
        'border-t border-border first:border-t-0 transition-colors',
        selected ? 'bg-icaria-100/40' : 'hover:bg-paper/40',
      )}
    >
      <Td align="center">
        <input
          type="checkbox"
          aria-label={`Seleccionar ${doc.title}`}
          checked={selected}
          onChange={onToggleSelect}
          className="h-4 w-4 accent-icaria-600 cursor-pointer"
        />
      </Td>
      <Td>
        <Badge tone={CATEGORY_TONE[doc.category]} dot>
          {CATEGORY_LABEL[doc.category]}
        </Badge>
      </Td>
      <Td>
        <div className="flex items-center gap-2.5 min-w-0">
          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium truncate">{doc.title}</p>
            <p className="font-mono text-[11px] text-muted-foreground truncate">{doc.filename}</p>
          </div>
        </div>
      </Td>
      <Td align="center">
        {doc.periodMonth && doc.periodYear ? (
          <span className="font-mono text-[12px] tabular-nums uppercase">
            {MONTH_SHORT[doc.periodMonth - 1]} {doc.periodYear}
          </span>
        ) : doc.periodYear ? (
          <span className="font-mono text-[12px] tabular-nums">{doc.periodYear}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
      <Td align="right">
        <span className="font-mono text-[12px] tabular-nums text-muted-foreground">
          {formatSize(doc.sizeBytes)}
        </span>
      </Td>
      <Td>
        <p className="text-[12px] text-muted-foreground leading-tight">
          {formatDate(doc.uploadedAt)}
        </p>
        <p className="text-[11px] text-muted-foreground leading-tight">por {doc.uploadedByName}</p>
      </Td>
      <Td align="right">
        <Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading}>
          <Download className="h-3.5 w-3.5" />
          {downloading ? 'Descargando…' : 'Descargar'}
        </Button>
      </Td>
    </tr>
  );
}

function EmptyState({ empty, hasFilters }: { empty: boolean; hasFilters: boolean }) {
  if (empty) {
    return (
      <div className="text-center py-12">
        <FileText className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-[14px] font-medium">No tienes documentos todavía</p>
        <p className="text-[12.5px] text-muted-foreground mt-1">
          Cuando RRHH suba una nómina, un contrato u otro documento, aparecerá aquí.
        </p>
      </div>
    );
  }
  return (
    <p className="text-[13px] text-muted-foreground py-4">
      {hasFilters ? 'Ningún documento coincide con los filtros.' : 'Sin documentos.'}
    </p>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex items-center gap-2 text-[12.5px]">
      <span className="font-mono text-[10px] font-medium uppercase tracking-cap text-muted-foreground">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-sm border border-input bg-card px-2.5 text-[12.5px] focus-visible:border-icaria-600 focus-visible:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'center' | 'right' }) {
  return (
    <th
      className={cn(
        'font-mono text-[12px] font-medium uppercase tracking-[0.08em] bg-paper px-5 py-3.5 text-muted-foreground border-b border-border',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
}: {
  children?: React.ReactNode;
  align?: 'center' | 'right';
}) {
  return (
    <td
      className={cn(
        'px-5 py-4 align-middle',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {children}
    </td>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTH_SHORT[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}
